import { HTMLAttributes, forwardRef } from "react";
import { Slot } from "radix-ui";
import classNames from "classnames/bind";
import Typography, { TypographyProps } from "app/pages/.shared/Typography/Typography";
import styles from "./Card.module.scss";

const cx = classNames.bind(styles);

export type CardVariant = "flat" | "elevated" | "ghost";
export type CardSpace = "xs" | "sm" | "md" | "lg" | "xl";
export type CardOrientation = "vertical" | "horizontal";

type CardBaseProps = {
	variant?: CardVariant;
	/** Layout direction of the direct children. */
	orientation?: CardOrientation;
	/** Inner padding of the card. */
	padding?: CardSpace;
	/** Gap between the card sections. */
	gap?: CardSpace;
	/** Merges the card styling onto its child (e.g. an `<a>`/`<button>` for a clickable card). */
	asChild?: boolean;
};

export type CardProps = CardBaseProps & HTMLAttributes<HTMLDivElement>;

export const Card = forwardRef<HTMLDivElement, CardProps>(
	(
		{
			variant = "flat",
			orientation = "vertical",
			padding = "md",
			gap = "md",
			asChild = false,
			className,
			style,
			...rest
		},
		ref
	) => {
		const Component = asChild ? Slot.Root : "div";

		return (
			<Component
				ref={ref}
				className={cx(
					"card",
					`card--${variant}`,
					`card--${orientation}`,
					`card--padding-${padding}`,
					`card--gap-${gap}`,
					className
				)}
				style={style}
				{...rest}
			/>
		);
	}
);
Card.displayName = "Card";

export type CardHeaderProps = HTMLAttributes<HTMLDivElement>;

export const CardHeader = forwardRef<HTMLDivElement, CardHeaderProps>(
	({ className, ...rest }, ref) => (
		<div ref={ref} className={cx("card__header", className)} {...rest} />
	)
);
CardHeader.displayName = "CardHeader";

type HeadingLevel = 2 | 3 | 4 | 5 | 6;

export type CardTitleProps = TypographyProps & {
	headingLevel?: HeadingLevel;
};

export const CardTitle = forwardRef<HTMLHeadingElement, CardTitleProps>(
	({ className, headingLevel = 3, children, ...rest }, ref) => (
		<Typography
			ref={ref}
			component={`h${headingLevel}`}
			isBold
			variant="large"
			className={cx("card__title", className)}
			{...rest}
		>
			{children}
		</Typography>
	)
);
CardTitle.displayName = "CardTitle";

export type CardDescriptionProps = TypographyProps;

export const CardDescription = forwardRef<HTMLParagraphElement, CardDescriptionProps>(
	({ className, children, ...rest }, ref) => (
		<Typography
			ref={ref}
			component="p"
			variant="regular"
			className={cx("card__description", className)}
			{...rest}
		>
			{children}
		</Typography>
	)
);
CardDescription.displayName = "CardDescription";

export type CardContentProps = HTMLAttributes<HTMLDivElement>;

export const CardContent = forwardRef<HTMLDivElement, CardContentProps>(
	({ className, ...rest }, ref) => (
		<div ref={ref} className={cx("card__content", className)} {...rest} />
	)
);
CardContent.displayName = "CardContent";

export type CardFooterProps = {
	/** Draws a line above the footer. Without it the card gap alone separates the footer from the content, so a vertical footer has no top padding. */
	divider?: boolean;
} & HTMLAttributes<HTMLDivElement>;

export const CardFooter = forwardRef<HTMLDivElement, CardFooterProps>(
	({ divider = true, className, ...rest }, ref) => (
		<div ref={ref} className={cx("card__footer", className)} data-divider={divider} {...rest} />
	)
);
CardFooter.displayName = "CardFooter";

export type CardMediaProps = HTMLAttributes<HTMLDivElement>;

/** Full-bleed wrapper for any media (image, linked image, carousel…); makes it span the card edges. */
export const CardMedia = forwardRef<HTMLDivElement, CardMediaProps>(
	({ className, ...rest }, ref) => (
		<div ref={ref} className={cx("card__media", className)} {...rest} />
	)
);
CardMedia.displayName = "CardMedia";
